// Shaping helpers for per-box docker status. Until HOME-243, this module
// also fetched each box's docker-status endpoint directly from the browser
// (fetchBoxStatus, HOME-184) — the client-side fan-out the Overview
// dashboard used to do. That network call has moved server-side: the
// portal API's `GET /overview` (infra/cdk/lambdas/portal-overview) now does
// the same per-box pull over mTLS and returns the data already shaped into
// `BoxStatus` below, via web/src/api/overviewApi.ts. This file keeps only
// the pure, non-network shaping/query helpers that BoxCard etc. still need
// to render whatever status the facade handed back.
//
// Contract the per-box service emits (unchanged, now consumed by the
// Lambda instead of the browser):
//   {
//     "host": "cal",
//     "reported_at": "2026-07-23T12:00:00Z",
//     "containers": [
//       { "name": "ollama", "container": "status-ollama-1", "state": "running",
//         "status": "Up 3 hours", "health": "healthy" }
//     ]
//   }

export interface ContainerRuntime {
    running: boolean;
    status?: string;
    health?: string;
}

export interface BoxStatus {
    reachable: boolean;
    reportedAt?: string;
    /** Keyed by container name. */
    containers: Record<string, ContainerRuntime>;
    /** Populated when the box couldn't be reached / parsed. */
    error?: string;
}

export type ServiceState = 'up' | 'down' | 'unknown';

/**
 * Resolve the display state of an expected container against a box's status.
 * - status not yet loaded / box unreachable → 'unknown' (we genuinely can't tell)
 * - present and running                     → 'up'
 * - present but not running, or expected-but-absent → 'down'
 */
export function resolveContainerState(status: BoxStatus | undefined, name: string): ServiceState {
    if (!status || !status.reachable) return 'unknown';
    const c = status.containers[name];
    if (!c) return 'down';          // expected but not reported → not running
    return c.running ? 'up' : 'down';
}

/** Container names the box reports that aren't in our expected list (config drift). */
export function extraContainers(status: BoxStatus | undefined, expected: string[]): string[] {
    if (!status || !status.reachable) return [];
    const known = new Set(expected);
    return Object.keys(status.containers).filter(n => !known.has(n)).sort();
}

// --- Service actions (HOME-188) ---------------------------------------
//
// POST {box.actionsUrl}/<name> — the status container's thin relay onto
// host-broker's own capability-scoped `POST /actions/<name>` (HOME-213).
// This client does no validation of its own: the broker is the sole source
// of truth for which action names/params are valid, so a rejection surfaces
// here exactly as the broker phrased it (its uniform opaque 403, or a
// real operational error).

const ACTION_TIMEOUT_MS = 10000;

export interface ActionResult {
    /** Whether the call round-tripped and came back 2xx. */
    ok: boolean;
    /** HTTP status code, when a response was received at all. */
    status?: number;
    /** Parsed JSON body, when the response was valid JSON. */
    data?: unknown;
    /** Populated when the call failed outright (network/timeout) or the
     *  body wasn't JSON — distinct from a valid-but-non-2xx broker response,
     *  which is still returned via `status`/`data` above. */
    error?: string;
}

/**
 * Invoke a service action, e.g. `postAction(box.actionsUrl, 'nexus-cache-invalidate', { repo: 'docker-hub' })`.
 * Never throws — network failures, timeouts, and non-2xx responses are all
 * reported via the returned `ActionResult` rather than a rejected promise,
 * so callers can render a result either way without a try/catch.
 */
export async function postAction(
    actionsUrl: string,
    name: string,
    body: Record<string, unknown>,
): Promise<ActionResult> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ACTION_TIMEOUT_MS);
    try {
        const res = await fetch(`${actionsUrl}/${name}`, {
            method: 'POST',
            signal: controller.signal,
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
        });
        let data: unknown;
        try {
            data = await res.json();
        } catch {
            return { ok: false, status: res.status, error: 'response was not valid JSON' };
        }
        return { ok: res.ok, status: res.status, data };
    } catch (e) {
        const msg = e instanceof DOMException && e.name === 'AbortError'
            ? 'timed out'
            : e instanceof Error ? e.message : String(e);
        return { ok: false, error: msg };
    } finally {
        clearTimeout(timer);
    }
}
